import React, { useState } from 'react';
import { PracticeAttendees } from './PracticeAttendees';
import { AttendeesPickerModal } from './AttendeesPickerModal';
import { Input } from '../../ui/Input';
import { Select } from '../../ui/Select';
import { SearchableSelect } from '../../ui/SearchableSelect';
import { mockData } from '../../../services/mockData';
import { trainingCategories, trainingProducts } from '../../../data/trainingProducts';
import { externalPartners } from '../../../data/practiceOptions';
import { formatEuro, parseAmount } from '../../../utils/currency';
import { practiceTotal } from '../../../utils/practices';
import { ChoiceGroup } from '../ChoiceGroup';
import { PracticeRateType, PracticeAttendee, PracticeClientType, PracticeDraft, PracticeErrors } from '../../../types/practice';
interface PracticeMainFieldsProps {
  draft: PracticeDraft;
  onChange: (changes: Partial<PracticeDraft>) => void;
  errors: PracticeErrors;
}
const sectionTitle = 'text-sm font-semibold text-seform-navy mb-4';
export function PracticeMainFields({
  draft,
  onChange,
  errors
}: PracticeMainFieldsProps) {
  const clientOptions = mockData.getClients().map(c => ({
    value: c.name,
    label: c.name
  }));
  const productOptions = trainingProducts.filter(p => p.category === draft.category).map(p => ({
    value: p.code,
    label: `${p.code} — ${p.type} · ${p.name}`
  }));
  const total = practiceTotal(draft);
  const [isPickerOpen, setIsPickerOpen] = useState(false);

  // Numero corsisti follows the people chosen for the course
  const setAttendees = (attendees: PracticeAttendee[]) => onChange({
    attendees,
    ...(attendees.length > 0 ? {
      participantsCount: attendees.length
    } : {})
  });

  // The three client types are alternatives: exactly one is always selected
  const option = (type: PracticeClientType, label: string, hint: string) => <label className="flex items-start gap-2 cursor-pointer" data-id="element-1410" data-mp="2lcqyhimefv1s">
      <input type="radio" name="practice-client-type" className="mt-0.5 border-slate-300 text-seform-orange focus:ring-seform-orange" checked={draft.clientType === type} onChange={() => onChange({
      clientType: type,
      attendees: []
    })} data-id="element-1411" data-mp="2lb66o0rgyq7m" />
      <span data-id="element-1412" data-mp="v7ma9tpedlz4">
        <span className="block text-sm font-medium text-slate-700" data-id="element-1413" data-mp="v85w3li64uif">{label}</span>
        <span className="block text-xs text-slate-500" data-id="element-1414" data-mp="qb5o351rdhoo">{hint}</span>
      </span>
    </label>;
  return <div className="divide-y divide-slate-100" data-id="element-1415" data-mp="2kt7z6wd0en0">
      <section className="pb-6" data-id="element-1416" data-mp="3tqunbrncxueg">
        <h3 className={sectionTitle} data-id="element-1417" data-mp="3trmbwiijerv8">Dati principali</h3>
        <div className="space-y-5" data-id="element-1418" data-mp="ca320sark7dr">
          <fieldset data-id="element-1419" data-mp="cb4nzrtbiea9">
            <legend className="block text-sm font-medium text-slate-700 mb-2" data-id="element-1420" data-mp="cbnv0dhfji9i">
              Tipologia cliente
            </legend>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-8" data-id="element-1421" data-mp="3fz6djv0kzi0n">
              {option('anagrafica', 'Azienda cliente in anagrafica', 'Azienda già presente in anagrafica')}
              {option('individuo', 'Individuo', 'Per privati cittadini')}
              {option('occasionale', 'Azienda Cliente Occasionale', 'Azienda non presente in anagrafica')}
            </div>
          </fieldset>
          {draft.clientType === 'anagrafica' && <SearchableSelect label="Denominazione aziendale" value={draft.companyName || ''} onChange={e => {
          const name = e.target.value;
          if (name === draft.companyName) return;
          // New company: reset attendees and open the selection window
          onChange({
            companyName: name,
            attendees: []
          });
          if (name) setIsPickerOpen(true);
        }} options={clientOptions} placeholder="Cerca in anagrafica azienda..." description="Recuperata automaticamente dall'anagrafica azienda" error={errors.companyName} required data-id="element-1422" data-mp="1lgv7k6i0ctq0" />}
          {draft.clientType === 'individuo' && <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-1423" data-mp="149x1bx5igfi3">
              <Input label="Nome" value={draft.firstName || ''} onChange={e => onChange({
            firstName: e.target.value
          })} error={errors.firstName} required data-id="element-1424" data-mp="zb3sii165co9" />
              <Input label="Cognome" value={draft.lastName || ''} onChange={e => onChange({
            lastName: e.target.value
          })} error={errors.lastName} required data-id="element-1425" data-mp="1ry6r1a5uhnei" />
            </div>}
          {draft.clientType === 'occasionale' && <Input label="Ragione Sociale" value={draft.businessName || ''} onChange={e => onChange({
          businessName: e.target.value
        })} error={errors.businessName} required data-id="element-1426" data-mp="7k66fwtb4qbr" />}
          <Input label="Email cliente" type="email" value={draft.clientEmail || ''} onChange={e => onChange({
          clientEmail: e.target.value
        })} placeholder="nome@esempio.it" description={draft.clientType === 'anagrafica' ? "Le email vengono inviate anche all'azienda e all'indirizzo Email Formazione in anagrafica" : 'Indirizzo a cui inviare le comunicazioni sul corso'} data-id="element-1427" data-mp="2e6lnjszwzlv3" />
          {(draft.clientType === 'anagrafica' && draft.companyName || draft.clientType === 'occasionale') && <PracticeAttendees draft={draft} onChange={setAttendees} onOpenPicker={() => setIsPickerOpen(true)} data-id="element-1428" data-mp="3azh0numf47b3" />}
        </div>
        {draft.clientType === 'anagrafica' && draft.companyName && <AttendeesPickerModal isOpen={isPickerOpen} companyName={draft.companyName} selected={draft.attendees || []} onClose={() => setIsPickerOpen(false)} onConfirm={list => {
        setAttendees(list);
        setIsPickerOpen(false);
      }} data-id="element-1429" data-mp="1gmr8pifm186c" />}
      </section>

      <section className="py-6" data-id="element-1430" data-mp="2ou5bmlru06jx">
        <h3 className={sectionTitle} data-id="element-1431" data-mp="2oxayrvgwgyd0">Informazioni corso</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-1432" data-mp="32teopckqqkjo">
          <Select label="Macrocategoria" value={draft.category} onChange={e => onChange({
          category: e.target.value,
          productCode: ''
        })} options={[{
          value: '',
          label: 'Seleziona macrocategoria...'
        }, ...trainingCategories.map(c => ({
          value: c,
          label: c
        }))]} error={errors.category} required data-id="element-1433" data-mp="2xvwgxpigmbd9" />
          <SearchableSelect label="Codice prodotto + Tipologia corso" value={draft.productCode} onChange={e => onChange({
          productCode: e.target.value
        })} options={productOptions} disabled={!draft.category} placeholder={draft.category ? productOptions.length > 0 ? 'Cerca codice o tipologia...' : 'Nessun prodotto in questa macrocategoria' : 'Seleziona prima la macrocategoria'} error={errors.productCode} required data-id="element-1434" data-mp="3gnp433aul2d2" />
        </div>

        <div className="mt-6" data-id="element-1435" data-mp="37j8quyugh3dd">
          <ChoiceGroup label="Tariffa (importo senza IVA)" name="practice-rate-type" value={draft.rateType} options={[{
          value: 'oraria',
          label: 'Tariffa oraria'
        }, {
          value: 'singola',
          label: 'Tariffa singola'
        }]} onChange={(v: PracticeRateType) => onChange({
          rateType: v
        })} data-id="element-1436" data-mp="37k0cbguc9kpx" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-4" data-id="element-1437" data-mp="3iylrghw5rmap">
          {draft.rateType === 'oraria' ? <>
              <Input label="Tariffa oraria" type="number" min={0} step="0.01" value={draft.hourlyRate ?? ''} onChange={e => onChange({
            hourlyRate: parseAmount(e.target.value)
          })} endContent={<span className="text-xs" data-id="element-1439" data-mp="302ltmqgi1oj7">€/h</span>} data-id="element-1438" data-mp="3e1d668fpr258" />
              <Input label="Monte ore corso" type="number" min={0} step="0.5" value={draft.courseHours ?? ''} onChange={e => onChange({
            courseHours: parseAmount(e.target.value)
          })} endContent={<span className="text-xs" data-id="element-1441" data-mp="2hc45kz6m3h46">ore</span>} data-id="element-1440" data-mp="2v8hl42rl58g0" />
            </> : <>
              <Input label="Costo singola iscrizione" type="number" min={0} step="0.01" value={draft.singleFee ?? ''} onChange={e => onChange({
            singleFee: parseAmount(e.target.value)
          })} endContent={<span className="text-xs" data-id="element-1443" data-mp="15rvwscqqpw2f">€</span>} data-id="element-1442" data-mp="rtfpokoyu3jr" />
              <Input label="Numero corsisti" type="number" min={1} step="1" value={draft.participantsCount ?? ''} onChange={e => onChange({
            participantsCount: parseAmount(e.target.value)
          })} data-id="element-1444" data-mp="92xzamrlvk1k" />
            </>}
          <div data-id="element-1445" data-mp="mvdz7ep7n1y9">
            <span className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-1446" data-mp="mvxhiwirvsct">
              Importo complessivo
            </span>
            <div className="rounded-xl bg-slate-100 px-4 py-2.5 text-sm font-semibold text-seform-navy tabular-nums" data-id="element-1447" data-mp="3qie7uwss0oz0">
              {total !== undefined ? formatEuro(total) : '—'}
            </div>
            <p className="text-xs text-slate-500 mt-1.5" data-id="element-1448" data-mp="1xk580ldm5xma">
              {draft.rateType === 'oraria' ? 'Tariffa oraria × monte ore corso' : 'Costo singola iscrizione × numero corsisti'}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-6" data-id="element-1449" data-mp="27gi4ts4ggrl9">
          <Select label="Provenienza" value={draft.source || ''} onChange={e => onChange({
          source: e.target.value || undefined
        })} description='Elenco "Esterni e Partner"' options={[{
          value: '',
          label: 'Seleziona...'
        }, ...externalPartners.map(p => ({
          value: p,
          label: p
        }))]} data-id="element-1450" data-mp="19wouiov0c7fe" />
          <Input label="Data" type="date" value={draft.date || ''} onChange={e => onChange({
          date: e.target.value || undefined
        })} data-id="element-1451" data-mp="r431sdfecwz1" />
        </div>
      </section>
    </div>;
}